import type { ComponentType } from 'react';
import { Car, Shirt, Camera, Sparkles, Mail, type LucideProps } from 'lucide-react';
import { InstagramIcon, FacebookIcon, TikTokIcon } from '../components/icons';
import { experiences } from './experiences';

type Icon = ComponentType<LucideProps>;

/* ------------------------------------------------------------------ */
/* Contact & socials                                                   */
/* ------------------------------------------------------------------ */

/** Build a wa.me link with an optional pre-filled message. */
export function whatsappLink(message?: string): string {
  const text =
    message ?? "Hi Flying Muse! I'd love to know more about your Santorini photoshoot experiences.";
  // wa.me wants the number in international format with no '+', spaces or zeros.
  return `https://wa.me/306985707174?text=${encodeURIComponent(text)}`;
}

export const contact = {
  phone: '+306985707174',
  whatsapp: whatsappLink(),
  email: 'info@flyingmuse.gr',
  location: 'Fira, Santorini, Greece',
  // Shareable map links to the studio pin so guests can find us / get directions.
  googleMaps: 'https://maps.app.goo.gl/qNKc99CHaADJpZsU7',
  appleMaps: 'https://maps.apple.com/?q=Flying%20Muse&ll=36.4121627,25.4370643',
  instagram: 'https://www.instagram.com/flyingmuse.gr',
  facebook: 'https://www.facebook.com/people/Flying-Muse/61590643118138',
  tiktok: 'https://www.tiktok.com/@flyingmuse.gr',
};

export interface SocialLink {
  label: string;
  href: string;
  Icon: ComponentType<{ className?: string }>;
}

export const socialLinks: SocialLink[] = [
  { label: 'Instagram', href: contact.instagram, Icon: InstagramIcon },
  { label: 'Facebook', href: contact.facebook, Icon: FacebookIcon },
  { label: 'TikTok', href: contact.tiktok, Icon: TikTokIcon },
  { label: 'Email', href: `mailto:${contact.email}`, Icon: Mail },
];

/* ------------------------------------------------------------------ */
/* What every experience includes                                      */
/* ------------------------------------------------------------------ */

export interface Inclusion {
  label: string;
  Icon: Icon;
}

export const inclusions: Inclusion[] = [
  { label: 'Pick up & drop off', Icon: Car },
  { label: 'Unlimited dress collection', Icon: Shirt },
  { label: 'Professional posing guidance', Icon: Camera },
  { label: 'Luxury editing', Icon: Sparkles },
];

/* ------------------------------------------------------------------ */
/* Navigation & footer                                                 */
/* ------------------------------------------------------------------ */

export interface NavLink {
  label: string;
  href: string;
}

export const navLinks: NavLink[] = [
  { label: 'Experiences', href: '/experiences' },
  { label: 'About', href: '/about' },
];

export interface FooterLink {
  label: string;
  href: string;
}

export const footerColumns: { title: string; links: FooterLink[] }[] = [
  {
    title: 'Experiences',
    links: experiences.map((exp) => ({ label: exp.title, href: `/${exp.slug}` })),
  },
  {
    title: 'Studio',
    links: [
      { label: 'All Experiences', href: '/experiences' },
      { label: 'About', href: '/about' },
      { label: 'Find the Studio', href: contact.googleMaps },
      { label: 'Book on WhatsApp', href: contact.whatsapp },
    ],
  },
];
